// src/app/pages/admin/BusquedaCandidatoTable.tsx
import { useEffect, useState } from 'react';
import styleScss from '../../pages/admin/styles.module.scss';
import { buscarCandidatoMarcacion } from '../../modules/apps/user-management/users-list/core/_requests';
import { LoadingBackdrop } from '../../components/LoadingSpinner/LoadingSpinner';
import {
	Button,
	Menu,
	MenuItem,
	Paper,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TablePagination,
	TableRow,
	TextField,
} from '@mui/material';

import { CandidatoBusqueda, getErrorMessage } from '../../pages/admin/EventosEmpleabilidad.helpers';
import { LISTA_TIPO_DOCUMENTO_CANDIDATO } from '../../models/TipoDocumentoOption';

//styles
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../modules/auth';

const headerCellStyle = { fontFamily: 'Poppins', fontSize: '16.25px', textAlign: 'center' as const };
const bodyCellStyle = { fontFamily: 'Poppins', fontSize: '14.5px', textAlign: 'center' as const };

interface BusquedaCandidatoTableProps {
	eventoId: string;
	embedded?: boolean;
	readOnly?: boolean;
	onVerHojaVida: (candidato: CandidatoBusqueda) => void;
	onVerAsociacion: (candidato: CandidatoBusqueda) => void;
	onError: (msg: string) => void;
}

const BusquedaCandidatoTable = ({
	eventoId,
	embedded = false,
	readOnly = false,
	onVerHojaVida,
	onVerAsociacion,
	onError,
}: BusquedaCandidatoTableProps) => {
	// Los filtros se guardan por evento para conservarlos al volver desde la hoja de vida.
	const storageKey = `marcacion-remision-filtros-${eventoId}`;
	const leerFiltrosGuardados = (): { tipo: string; numero: string } => {
		try {
			const raw = sessionStorage.getItem(storageKey);
			if (raw) {
				const parsed = JSON.parse(raw);
				return { tipo: parsed?.tipo || '', numero: parsed?.numero || '' };
			}
		} catch {
			// sessionStorage no disponible o valor inválido: se ignora
		}
		return { tipo: '', numero: '' };
	};
	const guardarFiltros = (tipo: string, numero: string) => {
		try {
			if (tipo || numero) sessionStorage.setItem(storageKey, JSON.stringify({ tipo, numero }));
			else sessionStorage.removeItem(storageKey);
		} catch {
			// sin persistencia, la búsqueda sigue funcionando
		}
	};

	const [tipoDocumento, setTipoDocumento] = useState(() => leerFiltrosGuardados().tipo);
	const [numeroDocumento, setNumeroDocumento] = useState(() => leerFiltrosGuardados().numero);
	const [candidatos, setCandidatos] = useState<CandidatoBusqueda[]>([]);
	const [buscado, setBuscado] = useState(false);
	const [loading, setLoading] = useState(false);
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(10);

	const cargar = (tipo?: string, numero?: string) => {
		setLoading(true);
		buscarCandidatoMarcacion(eventoId, tipo, numero)
			.then((response: any) => {
				setCandidatos(response?.data || []);
				setBuscado(true);
				setPage(0);
			})
			.catch((error: any) => {
				setCandidatos([]);
				setBuscado(true);
				setPage(0);
				onError(getErrorMessage(error));
			})
			.finally(() => setLoading(false));
	};

	useEffect(() => {
		// Los filtros restaurados (estado inicial) se consumen una sola vez: se limpia el storage.
		guardarFiltros('', '');
		cargar(tipoDocumento || undefined, numeroDocumento || undefined);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [eventoId]);

	const handleBuscar = () => {
		cargar(tipoDocumento || undefined, numeroDocumento || undefined);
	};

	const handleLimpiarFiltros = () => {
		setTipoDocumento('');
		setNumeroDocumento('');
		cargar();
	};
	const navigate = useNavigate();
	const { isAdminRole, isEmployerRole } = useAuth();
	const hojaVidaBasePath = isAdminRole() ? '/admin' : isEmployerRole() ? '/empleador' : '/consejero';

	const MenuAction = ({ candidato }: { candidato: CandidatoBusqueda }) => {
			const [anchorEl, setAnchorEl] = useState(null);
			const handleClick = (e: any) => setAnchorEl(e.currentTarget);
			const handleClose = () => setAnchorEl(null);
			const basePath = '';
	
			return (
				<>
					<Button onClick={handleClick}>
						<i className='bi bi-three-dots' style={{ fontSize: '20px' }}></i>
					</Button>
					<Menu anchorEl={anchorEl} keepMounted open={Boolean(anchorEl)} onClose={handleClose}>
						<MenuItem
							onClick={() => {
								handleClose();
								guardarFiltros(tipoDocumento, numeroDocumento);
								navigate(`${hojaVidaBasePath}/hoja-vida/${candidato.id}`);
							}}
						>
							<VisibilityIcon />
							&nbsp; Ver hoja de vida
						</MenuItem>
						<MenuItem
							onClick={() => {
								handleClose();
								onVerAsociacion(candidato);
							}}
						>
							<InfoOutlinedIcon />
							&nbsp; Marcar como remisión potencial
						</MenuItem>
						
					
					</Menu>
				</>
			);
		};

	return (
		<div className={embedded ? '' : 'p-4 shadow-4 rounded-3 mt-15'} style={embedded ? undefined : { backgroundColor: '#FFFF' }}>
			{!embedded && <LoadingBackdrop loading={loading} />}
			{embedded && <h3 className='mb-4'>Marcación remisión potencial</h3>}

			<div className='input-group d-flex flex-wrap align-items-center' style={{ gap: '10px' }}>
				<TextField
					select
					size='small'
					label='Tipo de documento'
					value={tipoDocumento}
					onChange={(e) => setTipoDocumento(e.target.value)}
					sx={{ minWidth: 250 }}
				>
					<MenuItem value=''>Seleccione...</MenuItem>
					{LISTA_TIPO_DOCUMENTO_CANDIDATO.map((doc) => (
						<MenuItem key={doc.code} value={doc.code}>
							{doc.value}
						</MenuItem>
					))}
				</TextField>
				<TextField
					size='small'
					label='Número de documento'
					value={numeroDocumento}
					onChange={(e) => setNumeroDocumento(e.target.value)}
				/>
				<Button variant='contained' onClick={handleBuscar}>
					Buscar
				</Button>
				<Button
					variant='outlined'
					disabled={!tipoDocumento && !numeroDocumento}
					onClick={handleLimpiarFiltros}
				>
					Limpiar filtros
				</Button>
			</div>

			<div className={`${styleScss['table-limit']}`} style={{ maxWidth: '100%', overflowWrap: 'break-word', overflowY: 'hidden' }}>
				<Stack padding={2}>
					<TableContainer component={Paper} style={{ overflowY: 'visible', overflowWrap: 'break-word' }}>
						<Table style={{ overflowY: 'visible' }}>
							<TableHead className={`${styleScss['th-custom']}`}>
								<TableRow>
									<TableCell style={headerCellStyle}><h4>Tipo de documento</h4></TableCell>
									<TableCell style={headerCellStyle}><h4>Número de documento</h4></TableCell>
									<TableCell style={headerCellStyle}><h4>Nombre completo</h4></TableCell>
									<TableCell style={headerCellStyle}><h4>Acciones</h4></TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{candidatos.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((c) => (
									<TableRow key={c.id}>
										<TableCell style={bodyCellStyle}>{c.tipo_documento}</TableCell>
										<TableCell style={bodyCellStyle}>{c.numero_documento}</TableCell>
										<TableCell style={bodyCellStyle}>{c.nombre_completo}</TableCell>										
										<TableCell style={{ ...{ fontFamily: 'Poppins', fontSize: '14.5px', textAlign: 'center' }, ...bodyCellStyle }}>
													<MenuAction candidato={c} />
										</TableCell> 
										{/* </TableCell> */}
											{/* <Stack direction='column' spacing={1} alignItems='center'>
												<Button size='small' variant='outlined' onClick={() => onVerHojaVida(c)}>
													Ver hoja de vida
												</Button>
												<Button size='small' variant='contained' disabled={readOnly} onClick={() => onVerAsociacion(c)}>
													Ver asociación candidato vacante
												</Button>
											</Stack> */}
										 {/* </TableCell>  */}
									</TableRow>
								))}
							</TableBody>
							{!loading && buscado && candidatos.length === 0 && (
								<tfoot>
									<TableRow>
										<TableCell colSpan={4}>
											<p className='text-center' style={{ width: '100%', textAlign: 'center' }}>
												No se encontraron registros
											</p>
										</TableCell>
									</TableRow>
								</tfoot>
							)}
						</Table>
						<TablePagination
							rowsPerPageOptions={[10, 20, 50]}
							component='div'
							style={{ overflow: 'visible', fontFamily: 'Poppins', fontSize: '14.5px' }}
							count={candidatos.length}
							rowsPerPage={rowsPerPage}
							page={page}
							labelRowsPerPage='Filas por página'
							onPageChange={(_e, newPage) => setPage(newPage)}
							onRowsPerPageChange={(e) => {
								setRowsPerPage(parseInt(e.target.value, 10));
								setPage(0);
							}}
						/>
					</TableContainer>
				</Stack>
			</div>
		</div>
	);
};

export default BusquedaCandidatoTable;